import { toast } from '@heidi/design-system/toast';
import { GET_TEMPLATE_REVERT_PREVIEW } from '@/components/chat/hooks/useTemplateRevertPreview';
import {
  GET_TEMPLATE_BY_ID,
  GET_TEMPLATE_CREATION_RECORD_BY_ID,
} from '@heidi/templates/contracts';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import {
  getTemplateCreationRecordByTemplateId,
  updateTemplateCreationRecord,
} from '@/pages/api/scribeAPIV2';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { track } from '@/lib/analytics/track';

/**
 * Undo a template's last version change: re-point the active version to the one
 * before the selected version. Resolved from `templateId` since that's all the
 * card has.
 */
export const useRevertTemplateEdit = () => {
  const queryClient = useQueryClient();
  const intl = useIntl();

  return useMutation({
    mutationFn: async (templateId: string) => {
      const {
        data: {
          template_creation_record: record,
          version_controls: rawVersionControls,
        },
      } = await getTemplateCreationRecordByTemplateId(templateId);

      // Sort oldest -> newest on the FE so version numbering doesn't rely on
      // the API preserving order.
      const versionControls = [...rawVersionControls].sort((a, b) =>
        a.created_at.localeCompare(b.created_at)
      );

      const recordId = record.id;
      const fromVersionControlId =
        record.selected_version_control_id ?? versionControls.at(-1)?.id;
      const selectedIndex = versionControls.findIndex(
        (versionControl) => versionControl.id === fromVersionControlId
      );
      const previousVersion =
        selectedIndex > 0 ? versionControls[selectedIndex - 1] : undefined;

      if (!recordId || !previousVersion) {
        throw new Error('NO_PREVIOUS_VERSION');
      }

      await updateTemplateCreationRecord({
        templateCreationRecordId: recordId,
        data: { selected_version_control_id: previousVersion.id },
      });

      return {
        templateId,
        recordId,
        fromVersionControlId,
        toVersionControlId: previousVersion.id,
        // 1-based position of the version we landed on.
        toVersionNumber: selectedIndex,
      };
    },
    onSuccess: ({ templateId, recordId, fromVersionControlId, toVersionControlId }) => {
      track({
        event_name: TrackingEventName.TemplateReverted,
        props: {
          template_id: templateId,
          source: 'ask_heidi_edit_card',
          from_version_control_id: fromVersionControlId ?? undefined,
          to_version_control_id: toVersionControlId,
        },
        options: { amplitude: false },
      });

      void queryClient.invalidateQueries({
        queryKey: [GET_TEMPLATE_BY_ID, { templateId }],
      });
      void queryClient.invalidateQueries({
        queryKey: [
          GET_TEMPLATE_CREATION_RECORD_BY_ID,
          { templateCreationRecordId: recordId },
        ],
      });
      void queryClient.invalidateQueries({
        queryKey: [GET_TEMPLATE_REVERT_PREVIEW, templateId],
      });
    },
    onError: () => {
      toast({
        tone: 'destructive',
        title: intl.formatMessage({
          defaultMessage: "Couldn't revert the template",
          id: 'pIUzlxQ5n1',
        }),
      });
    },
  });
};
